import { Col } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { IconFocusSmall } from '@amzn/stencil-react-icons';

import type { InsightDocument, OpenTaskRef } from 'src/types/starlight/AuditInsights';
import type { Messages } from 'src/v2/shared/i18n/useMessages';
import type { AuditInsightsMessageKey } from '../translations';
import { SectionHeading } from './SectionHeading';
import { accentBar } from './panelEdges';
import { StrengthsSection } from './StrengthsSection';
import { FailureThemesSection } from './FailureThemesSection';
import { RecurringSection } from './RecurringSection';

/**
 * Quotes the summary: the accent bar marks it as the audit's headline rather than one
 * more paragraph. Neutral blue, because the summary reports both halves of the reading.
 */
const SUMMARY_CALLOUT = accentBar('color.status.info-border');

interface ReadyInsightsProps {
  /** Scopes the link out to a task's own page. */
  businessUnitId: string;
  insights: InsightDocument;
  /** Read live rather than at generation, so it comes from the response, not the document. */
  openTasks: OpenTaskRef[];
  openTasksUnavailable: boolean;
  /** What each question asked, keyed by id. The insight carries titles but not wording. */
  questionTextById: ReadonlyMap<string, string>;
  messages: Messages<AuditInsightsMessageKey>;
}

/**
 * The generated reading of a completed audit, in the order the reader needs it: the
 * headline first, then what held up, then what did not.
 *
 * Every section below the summary is optional, because generation keeps a partial insight
 * rather than discarding one over a missing section.
 */
export function ReadyInsights({
  businessUnitId,
  insights,
  openTasks,
  openTasksUnavailable,
  questionTextById,
  messages,
}: ReadyInsightsProps): JSX.Element {
  const { t, f } = messages;

  return (
    <Col gridGap="dimensions.spacing.400">
      <Col gridGap="dimensions.spacing.200">
        <SectionHeading icon={<IconFocusSmall />} label={t('executiveSummaryTitle')} />
        <Col
          backgroundColor="color.surface.bg-muted"
          padding="dimensions.spacing.300"
          css={SUMMARY_CALLOUT}
        >
          <Text>{insights.executiveSummary}</Text>
        </Col>
      </Col>

      <StrengthsSection
        strengths={insights.strengths}
        score={insights.score}
        heading={t('strengthsTitle')}
        formatPassCount={(passed, total) => f('strengthsPassCount', { passed, total })}
      />

      <FailureThemesSection
        themes={insights.failureThemes}
        heading={t('failureThemesTitle')}
        formatQuestionId={(id) => f('questionIdChip', { id })}
      />

      <RecurringSection
        businessUnitId={businessUnitId}
        recurringFailures={insights.recurringFailures}
        openTasks={openTasks}
        openTasksUnavailable={openTasksUnavailable}
        questionTextById={questionTextById}
        messages={messages}
      />
    </Col>
  );
}
